import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../shared/api';
import { formatMoney, formatMoneyAxis, formatNumber } from '../shared/formatting';
import { BrandSpinner } from '../shared/BrandSpinner';
import { LineChart } from '../shared/charts';

type Channel = 'ADMS' | 'UREDI' | 'DIGITAL';
type Period = 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'H1' | 'H2' | 'Y' | 'M1' | 'M2' | 'M3' | 'M4' | 'M5' | 'M6' | 'M7' | 'M8' | 'M9' | 'M10' | 'M11' | 'M12';
type Scope = 'consolidated' | 'ado_only' | 'adms_only';

type OpexGroup = {
    group: string;
    label: string;
    iznos: number;
    konti: Array<{ konto: string; naziv: string | null; iznos: number }>;
    is_forecast?: boolean;
};

type ForecastBlock = {
    method: string;
    bridge_revenue_eur: number;
    bridge_to_promet_multiplier: number;
    promet_forecast_eur: number;
    eta_total: number;
    eta_stddev: number;
    eta_cov_pct: number | null;
    ruc_forecast_eur: number;
    ruc_forecast_low_eur: number;
    ruc_forecast_high_eur: number;
    by_kategorija: Record<string, number>;
    by_kanal: Record<string, number>;
    matrix: Record<string, Record<string, number>>;
    cogs_forecast_eur: number;
    sample_months: number;
    sample_label: string;
    note: string;
};

type DataSource = {
    tabak_available: boolean;
    mode: 'actual' | 'forecast';
    note: string;
};

type PeriodPayload = {
    filters: {
        year: number; period: string; scope: Scope; months: number[];
        date_from: string; date_to: string;
        tabak_period_label: string | null; includes_adms: boolean;
    };
    revenue: {
        bruto_promet: number;
        bridge: {
            ado_klasa_7: number;
            adms_klasa_7: number;
            ado_buyback_gross: number;
            computed_total: number;
            tabak_actual: number;
            delta: number;
            note: string;
        };
        by_channel: Record<Channel, number>;
        by_kategorija: Record<string, number>;
        implied_margin_pct: Record<string, number | null>;
    };
    cogs: { estimated_eur: number; method: string; formula: string; note: string };
    tabak: {
        by_channel: Record<Channel, Record<string, number>>;
        bruto_ruc: number;
        plan_ruc: number;
        plan_realizacija_pct: number | null;
        bruto_promet: number;
    };
    b2b: {
        slovenija: { count: number; total_eur: number };
        bugarska: { count: number; total_eur: number };
        intercompany: { count: number; total_eur: number };
    };
    opex: {
        breakdown: OpexGroup[];
        total_eur: number;
        adms_provizija_eliminated: boolean;
        adms_provizija_eur: number;
        effective_provizija_eur: number;
    };
    pnl: {
        revenue_eur: number; cogs_eur: number;
        gross_margin_eur: number; gross_margin_pct: number | null;
        opex_eur: number; intercompany_provizija_eur: number;
        ebitda_eur: number; ebitda_pct: number | null;
        net_eur: number; net_pct: number | null;
    };
    knjigovodstveni: {
        method: 'spread_historical' | 'modul_2_pipeline' | 'consolidated' | string;
        prihod_eur: number;
        cogs_eur: number;
        cogs_method?: string;
        tabak_margin_used?: number;
        eta_historical_pct?: number;
        eta_source?: string;
        racuni_eur?: number;
        buyback_eur?: number;
        scrap_margin_eur?: number;
        tabak_zalog_mjenj_eur?: number;
        gross_margin_eur: number;
        opex_eur: number;
        provizija_adms_eur?: number;
        amortizacija_eur: number;
        financijski_rashodi_eur: number;
        ebitda_eur: number;
        net_eur: number;
        gross_margin_pct: number | null;
        ebitda_pct: number | null;
        net_pct: number | null;
        intercompany_eliminated_eur?: number;
        per_company?: Record<string, Record<string, number>>;
        note: string;
        error?: string;
    } | null;
    forecast: ForecastBlock;
    data_source: DataSource;
    net_eur: number;
    net_margin_pct: number | null;
};

type MonthlyRow = { mjesec: number; month_label: string; ADMS: number; UREDI: number; DIGITAL: number; total: number };
type BranchRow = { poslovnica: string; kanal: string; grad: string | null; voditelj: string | null; ruc: number };

type Payload = {
    current: PeriodPayload;
    compare: PeriodPayload | null;
    monthly_series: MonthlyRow[];
    top_branches: BranchRow[];
};

const PERIOD_LABELS: Record<Period, { hr: string; en: string }> = {
    Q1: { hr: 'Q1', en: 'Q1' }, Q2: { hr: 'Q2', en: 'Q2' }, Q3: { hr: 'Q3', en: 'Q3' }, Q4: { hr: 'Q4', en: 'Q4' },
    H1: { hr: 'H1', en: 'H1' }, H2: { hr: 'H2', en: 'H2' }, Y: { hr: 'Cijela godina', en: 'Full year' },
    M1: { hr: 'Siječanj', en: 'January' }, M2: { hr: 'Veljača', en: 'February' }, M3: { hr: 'Ožujak', en: 'March' },
    M4: { hr: 'Travanj', en: 'April' }, M5: { hr: 'Svibanj', en: 'May' }, M6: { hr: 'Lipanj', en: 'June' },
    M7: { hr: 'Srpanj', en: 'July' }, M8: { hr: 'Kolovoz', en: 'August' }, M9: { hr: 'Rujan', en: 'September' },
    M10: { hr: 'Listopad', en: 'October' }, M11: { hr: 'Studeni', en: 'November' }, M12: { hr: 'Prosinac', en: 'December' },
};

const KAT_LABELS: Array<{ key: string; hr: string; en: string }> = [
    { key: 'Otkup', hr: 'Otkup', en: 'Buyback' },
    { key: 'Invest.', hr: 'Investicijsko', en: 'Investment' },
    { key: 'Zalog', hr: 'Zalog', en: 'Pawn' },
    { key: 'Mjenjač.', hr: 'Mjenjačnica', en: 'Exchange' },
];

const CHANNEL_META: Record<Channel, { hr: string; en: string; accent: string; icon: string }> = {
    ADMS: { hr: 'Poslovnice (ADMS)', en: 'Branches (ADMS)', accent: 'var(--ad-acc)', icon: '🏛' },
    UREDI: { hr: 'Uredi', en: 'Offices', accent: '#6f42c1', icon: '🏢' },
    DIGITAL: { hr: 'Webshop', en: 'Webshop', accent: '#2f9e44', icon: '🌐' },
};

function delta(a: number, b: number): { abs: number; pct: number | null; tone: 'pos' | 'neg' | 'neutral' } {
    const d = a - b;
    const pct = Math.abs(b) > 0.01 ? (d / Math.abs(b)) * 100 : null;
    return { abs: d, pct, tone: d > 0 ? 'pos' : d < 0 ? 'neg' : 'neutral' };
}

export function AdoEkonomskiPnl({ token, locale }: { token: string; locale: 'hr' | 'en' }) {
    const t = (hr: string, en: string): string => (locale === 'en' ? en : hr);
    const [year, setYear] = useState(2026);
    const [period, setPeriod] = useState<Period>('Q1');
    const [scope, setScope] = useState<Scope>('consolidated');
    const [comparePreset, setComparePreset] = useState<'yoy' | 'prev' | 'none'>('yoy');
    const [payload, setPayload] = useState<Payload | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const compareParams = useMemo(() => {
        if (comparePreset === 'none') return null;
        if (comparePreset === 'yoy') return { compare_year: year - 1, compare_period: period };
        const prevMap: Record<Period, { year: number; period: Period }> = {
            Q1: { year: year - 1, period: 'Q4' }, Q2: { year, period: 'Q1' },
            Q3: { year, period: 'Q2' }, Q4: { year, period: 'Q3' },
            H1: { year: year - 1, period: 'H2' }, H2: { year, period: 'H1' },
            Y: { year: year - 1, period: 'Y' },
            M1: { year: year - 1, period: 'M12' }, M2: { year, period: 'M1' }, M3: { year, period: 'M2' },
            M4: { year, period: 'M3' }, M5: { year, period: 'M4' }, M6: { year, period: 'M5' },
            M7: { year, period: 'M6' }, M8: { year, period: 'M7' }, M9: { year, period: 'M8' },
            M10: { year, period: 'M9' }, M11: { year, period: 'M10' }, M12: { year, period: 'M11' },
        };
        const p = prevMap[period];
        return { compare_year: p.year, compare_period: p.period };
    }, [comparePreset, year, period]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const q = new URLSearchParams({ year: String(year), period, scope });
            if (compareParams) {
                q.set('compare_year', String(compareParams.compare_year));
                q.set('compare_period', compareParams.compare_period);
            }
            const res = await api<Payload>(`/pnl/ado-ekonomski?${q.toString()}`, token);
            setPayload(res);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }, [token, year, period, scope, compareParams]);

    useEffect(() => { void load(); }, [load]);

    if (loading) return <div className="ado-pnl__loading"><BrandSpinner /></div>;
    if (error) return <div className="ado-pnl__error">{error}</div>;
    if (!payload) return null;

    const c = payload.current;
    const cmp = payload.compare;
    const periodLabelHr = PERIOD_LABELS[period].hr;
    const bridge = c.revenue.bridge;

    return (
        <div className="ado-pnl">
            {/* Controls */}
            <div className="ado-pnl__controls">
                <div className="ado-pnl__scope-toggle" role="radiogroup">
                    <button type="button" className={`ado-pnl__scope-btn${scope === 'consolidated' ? ' is-active' : ''}`} onClick={() => setScope('consolidated')}>
                        {t('Konsolidirano', 'Consolidated')}
                    </button>
                    <button type="button" className={`ado-pnl__scope-btn${scope === 'ado_only' ? ' is-active' : ''}`} onClick={() => setScope('ado_only')}>
                        {t('Samo ADO', 'ADO only')}
                    </button>
                    <button type="button" className={`ado-pnl__scope-btn${scope === 'adms_only' ? ' is-active' : ''}`} onClick={() => setScope('adms_only')}>
                        {t('Samo ADMS', 'ADMS only')}
                    </button>
                </div>
                <label className="ado-pnl__ctrl">
                    {t('Godina', 'Year')}
                    <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                        {[2024, 2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                </label>
                <label className="ado-pnl__ctrl">
                    {t('Razdoblje', 'Period')}
                    <select value={period} onChange={(e) => setPeriod(e.target.value as Period)}>
                        <optgroup label={t('Kvartali', 'Quarters')}>
                            {(['Q1', 'Q2', 'Q3', 'Q4'] as Period[]).map((p) => <option key={p} value={p}>{p}</option>)}
                        </optgroup>
                        <optgroup label={t('Polugodišta', 'Half-years')}>
                            {(['H1', 'H2'] as Period[]).map((p) => <option key={p} value={p}>{p}</option>)}
                        </optgroup>
                        <option value="Y">{t('Cijela godina', 'Full year')}</option>
                        <optgroup label={t('Mjeseci', 'Months')}>
                            {(['M1', 'M2', 'M3', 'M4', 'M5', 'M6', 'M7', 'M8', 'M9', 'M10', 'M11', 'M12'] as Period[]).map((p) => (
                                <option key={p} value={p}>{PERIOD_LABELS[p].hr}</option>
                            ))}
                        </optgroup>
                    </select>
                </label>
                <label className="ado-pnl__ctrl">
                    {t('Usporedba', 'Compare')}
                    <select value={comparePreset} onChange={(e) => setComparePreset(e.target.value as typeof comparePreset)}>
                        <option value="yoy">{t('YoY (prošla godina)', 'YoY (previous year)')}</option>
                        <option value="prev">{t('Prethodno razdoblje', 'Previous period')}</option>
                        <option value="none">{t('Bez usporedbe', 'No compare')}</option>
                    </select>
                </label>
            </div>

            {/* Hero */}
            <section className="ado-pnl__hero">
                <div className="ado-pnl__hero-pattern" aria-hidden="true" />
                <div className="ado-pnl__hero-content">
                    <div>
                        <p className="ado-pnl__eyebrow">
                            {scope === 'consolidated' ? t('Ekonomski P&L — konsolidirano (ADO + ADMS)', 'Economic P&L — consolidated (ADO + ADMS)')
                             : scope === 'ado_only' ? t('Ekonomski P&L — samo ADO', 'Economic P&L — ADO only')
                             : t('Ekonomski P&L — samo ADMS', 'Economic P&L — ADMS only')}
                            {c.data_source.mode === 'forecast' ? (
                                <span style={{ marginLeft: 10, padding: '2px 8px', borderRadius: 6, background: '#fef3c7', color: '#92400e', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em' }}>
                                    {t('🔮 PREDVIĐENO', '🔮 FORECAST')}
                                </span>
                            ) : null}
                        </p>
                        <h2 className="ado-pnl__hero-figure">{formatMoney(c.pnl.net_eur, locale)}</h2>
                        <p className="ado-pnl__hero-sub">
                            {c.data_source.mode === 'forecast' ? t('Predviđen Net za', 'Forecast Net for') : t('Net rezultat za', 'Net result for')} <strong>{periodLabelHr} {year}</strong>
                            {cmp ? (() => {
                                const d = delta(c.pnl.net_eur, cmp.pnl.net_eur);
                                return (
                                    <span className={`ado-pnl__delta tone-${d.tone}`}>
                                        {d.tone === 'pos' ? '▲' : d.tone === 'neg' ? '▼' : '·'} {formatMoney(d.abs, locale)}
                                        {d.pct !== null ? ` (${d.pct >= 0 ? '+' : ''}${d.pct.toFixed(1)}%)` : ''} vs {cmp.filters.period} {cmp.filters.year}
                                    </span>
                                );
                            })() : null}
                            {c.pnl.net_pct !== null ? <span className="muted" style={{ marginLeft: 8 }}>· {c.pnl.net_pct.toFixed(2)}% {t('marže', 'margin')}</span> : null}
                        </p>
                    </div>
                    <div className="ado-pnl__hero-side">
                        {c.tabak.plan_realizacija_pct !== null && (
                            <div className="ado-pnl__hero-stat">
                                <span className="ado-pnl__eyebrow">{t('Realizacija plana RUC', 'RUC plan realisation')}</span>
                                <strong className={c.tabak.plan_realizacija_pct >= 100 ? 'tone-pos' : 'tone-neg'}>{c.tabak.plan_realizacija_pct.toFixed(1)}%</strong>
                                <span className="muted" style={{ fontSize: '0.78rem' }}>Plan {formatMoneyAxis(c.tabak.plan_ruc)}</span>
                            </div>
                        )}
                        <div className="ado-pnl__hero-stat">
                            <span className="ado-pnl__eyebrow">{t('Bruto Promet', 'Gross revenue')}</span>
                            <strong>{formatMoney(c.pnl.revenue_eur, locale)}</strong>
                            {cmp ? (() => {
                                const d = delta(c.pnl.revenue_eur, cmp.pnl.revenue_eur);
                                return <span className={`muted ado-pnl__kpi-delta tone-${d.tone}`} style={{ fontSize: '0.78rem' }}>{d.pct !== null ? `${d.pct >= 0 ? '+' : ''}${d.pct.toFixed(1)}%` : '—'}</span>;
                            })() : null}
                        </div>
                    </div>
                </div>
            </section>

            {/* P&L waterfall — main hierarchy */}
            <section className="ado-pnl__section">
                <div className="ado-pnl__section-heading">
                    <h3>{t('P&L hijerarhija', 'P&L hierarchy')}</h3>
                    <p className="muted">{t('Prihod → COGS (ekstrapolirano) → Bruto marža → OPEX → EBITDA → Net.', 'Revenue → COGS (extrapolated) → Gross margin → OPEX → EBITDA → Net.')}</p>
                </div>
                <div className="ado-pnl__waterfall">
                    <PnlLine label={t('Prihod (Bruto Promet)', 'Revenue (Gross revenue)')} value={c.pnl.revenue_eur} compare={cmp?.pnl.revenue_eur} locale={locale} bold tone="pos" />
                    <PnlLine label={t('COGS (ekstrapolirano: Promet − RUC)', 'COGS (extrapolated: Revenue − RUC)')} value={-c.pnl.cogs_eur} compare={cmp ? -cmp.pnl.cogs_eur : undefined} locale={locale} tone="neg" indent />
                    <PnlLine label={t('= Bruto marža (RUC)', '= Gross margin (RUC)')} value={c.pnl.gross_margin_eur} compare={cmp?.pnl.gross_margin_eur} locale={locale} bold tone="pos" subtotal pct={c.pnl.gross_margin_pct} />
                    <PnlLine label={t('OPEX (klasa 4, RRIF grupe)', 'OPEX (class 4, RRIF groups)')} value={-c.pnl.opex_eur} compare={cmp ? -cmp.pnl.opex_eur : undefined} locale={locale} tone="neg" indent />
                    {c.pnl.intercompany_provizija_eur !== 0 ? (
                        <PnlLine label={t('Intercompany provizija ADO → ADMS', 'Intercompany commission ADO → ADMS')} value={-c.pnl.intercompany_provizija_eur} compare={cmp ? -cmp.pnl.intercompany_provizija_eur : undefined} locale={locale} tone="neg" indent />
                    ) : null}
                    <PnlLine label={t('= EBITDA', '= EBITDA')} value={c.pnl.ebitda_eur} compare={cmp?.pnl.ebitda_eur} locale={locale} bold subtotal pct={c.pnl.ebitda_pct} tone={c.pnl.ebitda_eur >= 0 ? 'pos' : 'neg'} />
                </div>
                {scope === 'consolidated' && c.opex.adms_provizija_eur > 0 ? (
                    <p className="muted" style={{ fontSize: '0.82rem', marginTop: '0.5rem' }}>
                        ℹ️ {t('U konsolidiranom view-u, ADMS provizija', 'In consolidated view, ADMS commission')} ({formatMoney(c.opex.adms_provizija_eur, locale)}) {t('je intercompany i eliminirana je iz EBITDA-e.', 'is intercompany and eliminated from EBITDA.')}
                    </p>
                ) : null}
            </section>

            {/* Revenue Breakdown — što čini Bruto Promet (bridge formula) */}
            <section className="ado-pnl__section">
                <div className="ado-pnl__section-heading">
                    <h3>{t('Što čini Bruto Promet', 'What makes up Gross Revenue')}</h3>
                    <p className="muted">{t('Računovodstveni izvor (GL klasa 7 + buyback) vs. tabak operativni split po kanalu + razrada po kategoriji s implied margin.', 'Accounting source (GL class 7 + buyback) vs. Tabak operational split by channel + category breakdown with implied margin.')}</p>
                </div>

                {/* Promet po kategoriji + implied margin */}
                <article className="ado-pnl__revenue-card" style={{ marginBottom: '0.85rem' }}>
                    <p className="ado-pnl__eyebrow">{t('Razrada po kategoriji (Promet + RUC + implied margin)', 'By category (Revenue + RUC + implied margin)')}</p>
                    <table className="ado-pnl__bridge-table">
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left', padding: '0.35rem 0.4rem' }}>{t('Kategorija', 'Category')}</th>
                                <th style={{ textAlign: 'right', padding: '0.35rem 0.4rem' }}>{t('Promet', 'Revenue')}</th>
                                <th style={{ textAlign: 'right', padding: '0.35rem 0.4rem' }}>{t('Bruto RUC', 'Gross margin')}</th>
                                <th style={{ textAlign: 'right', padding: '0.35rem 0.4rem' }}>{t('Margin', 'Margin')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {KAT_LABELS.map((kat) => {
                                const promet = c.revenue.by_kategorija[kat.key] ?? 0;
                                const ruc = (['ADMS', 'UREDI', 'DIGITAL'] as Channel[]).reduce((s, ch) => s + (c.tabak.by_channel[ch]?.[kat.key] ?? 0), 0);
                                const margin = c.revenue.implied_margin_pct[kat.key];
                                return (
                                    <tr key={kat.key}>
                                        <td><strong>{locale === 'en' ? kat.en : kat.hr}</strong></td>
                                        <td style={{ textAlign: 'right' }}>{formatMoney(promet, locale)}</td>
                                        <td style={{ textAlign: 'right' }}>{formatMoney(ruc, locale)}</td>
                                        <td style={{ textAlign: 'right', color: 'var(--ad-ink)', fontWeight: 600 }}>{margin !== null ? `${margin.toFixed(2)}%` : '—'}</td>
                                    </tr>
                                );
                            })}
                            <tr className="ado-pnl__bridge-sum">
                                <td><strong>{t('Total', 'Total')}</strong></td>
                                <td style={{ textAlign: 'right' }}><strong>{formatMoney(c.pnl.revenue_eur, locale)}</strong></td>
                                <td style={{ textAlign: 'right' }}><strong>{formatMoney(c.tabak.bruto_ruc, locale)}</strong></td>
                                <td style={{ textAlign: 'right', color: 'var(--ad-ink)' }}><strong>{c.pnl.revenue_eur > 0 ? `${(c.tabak.bruto_ruc / c.pnl.revenue_eur * 100).toFixed(2)}%` : '—'}</strong></td>
                            </tr>
                        </tbody>
                    </table>
                    <p className="muted" style={{ fontSize: '0.78rem', marginTop: '0.4rem' }}>
                        {t('Otkup ≈ ADO buyback gross. Invest ≈ ADO+ADMS klasa 7. Zalog+Mjenjač = ostatak (split po tabak RUC omjeru).', 'Buyback ≈ ADO buyback gross. Invest ≈ ADO+ADMS class 7. Pawn+Exchange = remainder (split by tabak RUC ratio).')}
                    </p>
                </article>

                <div className="ado-pnl__revenue-grid">
                    <article className="ado-pnl__revenue-card">
                        <p className="ado-pnl__eyebrow">{t('Računovodstveni bridge (iz GL)', 'Accounting bridge (from GL)')}</p>
                        <table className="ado-pnl__bridge-table">
                            <tbody>
                                <tr><td>ADO klasa 7 (prihod)</td><td>{formatMoney(bridge.ado_klasa_7, locale)}</td></tr>
                                {bridge.adms_klasa_7 > 0 ? <tr><td>ADMS klasa 7 (prihod)</td><td>{formatMoney(bridge.adms_klasa_7, locale)}</td></tr> : null}
                                <tr><td>ADO buyback gross (otkup zlata)</td><td>{formatMoney(bridge.ado_buyback_gross, locale)}</td></tr>
                                <tr className="ado-pnl__bridge-sum"><td><strong>{t('Bridge total', 'Bridge total')}</strong></td><td><strong>{formatMoney(bridge.computed_total, locale)}</strong></td></tr>
                                <tr><td className="muted">{t('Tabak Promet (stvarno)', 'Tabak revenue (actual)')}</td><td className="muted">{formatMoney(bridge.tabak_actual, locale)}</td></tr>
                                <tr><td className="muted">{t('Razlika (zalog/mjenjač spreads + zaokruživanja)', 'Delta (pawn/exchange spreads + rounding)')}</td><td className={`muted tone-${bridge.delta >= 0 ? 'pos' : 'neg'}`}>{formatMoney(bridge.delta, locale)}</td></tr>
                            </tbody>
                        </table>
                        <p className="muted" style={{ fontSize: '0.78rem', marginTop: '0.4rem' }}>{bridge.note}</p>
                    </article>
                    <article className="ado-pnl__revenue-card">
                        <p className="ado-pnl__eyebrow">{t('Tabak operativni split (po kanalu)', 'Tabak operational split (by channel)')}</p>
                        <table className="ado-pnl__bridge-table">
                            <tbody>
                                {(Object.entries(c.revenue.by_channel) as Array<[Channel, number]>).map(([ch, v]) => {
                                    const pct = c.pnl.revenue_eur > 0 ? (v / c.pnl.revenue_eur) * 100 : 0;
                                    return (
                                        <tr key={ch}>
                                            <td>{CHANNEL_META[ch]?.icon} {locale === 'en' ? CHANNEL_META[ch]?.en : CHANNEL_META[ch]?.hr}</td>
                                            <td>{formatMoney(v, locale)} <span className="muted" style={{ fontSize: '0.78rem' }}>({pct.toFixed(1)}%)</span></td>
                                        </tr>
                                    );
                                })}
                                <tr className="ado-pnl__bridge-sum"><td><strong>{t('Total', 'Total')}</strong></td><td><strong>{formatMoney(c.pnl.revenue_eur, locale)}</strong></td></tr>
                            </tbody>
                        </table>
                    </article>
                </div>
            </section>

            {/* Knjigovodstveni P&L — usporedba s tabak ekonomskim */}
            {c.knjigovodstveni && !c.knjigovodstveni.error ? (() => {
                const k = c.knjigovodstveni!;
                const delta = c.pnl.ebitda_eur - k.ebitda_eur;
                const methodLabel = k.method === 'mng-bucket' ? t('Modul 2 P&L (mng-bucket — ground truth)', 'Module 2 P&L (mng-bucket — ground truth)')
                    : k.method === 'spread' ? t('Spread aproksimacija (Prihod × (1 − tabak margin))', 'Spread approximation (Revenue × (1 − tabak margin))')
                    : t('Konsolidirano (ADO spread + ADMS Modul 2, eliminirano 506k)', 'Consolidated (ADO spread + ADMS Module 2, 506k eliminated)');
                return (
                    <section className="ado-pnl__section">
                        <div className="ado-pnl__section-heading">
                            <h3>{t('Usporedba: Tabak (RUC procjena) vs Knjigovodstveno', 'Comparison: Tabak (RUC estimate) vs Accounting')}</h3>
                            <p className="muted">{methodLabel}.</p>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.85rem' }}>
                            <article className="ado-pnl__revenue-card">
                                <p className="ado-pnl__eyebrow">{t('Tabak (RUC procjena)', 'Tabak (RUC estimate)')}</p>
                                <table className="ado-pnl__bridge-table">
                                    <tbody>
                                        <tr><td>{t('Promet', 'Revenue')}</td><td style={{ textAlign: 'right' }}>{formatMoney(c.pnl.revenue_eur, locale)}</td></tr>
                                        <tr><td>{t('RUC (gross margin)', 'RUC (gross margin)')}</td><td style={{ textAlign: 'right' }}>{formatMoney(c.tabak.bruto_ruc, locale)}</td></tr>
                                        <tr><td>OPEX</td><td style={{ textAlign: 'right' }}>−{formatMoney(c.opex.total_eur, locale)}</td></tr>
                                        {c.pnl.intercompany_provizija_eur !== 0 ? (
                                            <tr><td>{t('Provizija ADO→ADMS', 'Commission')}</td><td style={{ textAlign: 'right' }}>−{formatMoney(c.pnl.intercompany_provizija_eur, locale)}</td></tr>
                                        ) : null}
                                        <tr className="ado-pnl__bridge-sum"><td><strong>EBITDA</strong></td><td style={{ textAlign: 'right' }}><strong>{formatMoney(c.pnl.ebitda_eur, locale)}</strong></td></tr>
                                        <tr style={{ color: 'rgba(0,0,0,0.55)', fontSize: '0.82rem' }}><td>margin</td><td style={{ textAlign: 'right' }}>{c.pnl.ebitda_pct !== null ? `${c.pnl.ebitda_pct.toFixed(2)}%` : '—'}</td></tr>
                                    </tbody>
                                </table>
                            </article>
                            <article className="ado-pnl__revenue-card">
                                <p className="ado-pnl__eyebrow">{t('Knjigovodstveno', 'Accounting')}</p>
                                <table className="ado-pnl__bridge-table">
                                    <tbody>
                                        <tr><td>{t('Prihod', 'Revenue')}</td><td style={{ textAlign: 'right' }}>{formatMoney(k.prihod_eur, locale)}</td></tr>
                                        <tr>
                                            <td>COGS {k.cogs_method === 'spread_from_tabak' ? <span className="muted" style={{ fontSize: '0.7rem' }}>({t('spread iz tabaka', 'spread from tabak')})</span> : null}</td>
                                            <td style={{ textAlign: 'right' }}>−{formatMoney(k.cogs_eur, locale)}</td>
                                        </tr>
                                        <tr><td>OPEX</td><td style={{ textAlign: 'right' }}>−{formatMoney(k.opex_eur, locale)}</td></tr>
                                        {k.provizija_adms_eur && k.provizija_adms_eur > 0 ? (
                                            <tr><td>{t('Provizija ADO→ADMS', 'Commission')}</td><td style={{ textAlign: 'right' }}>−{formatMoney(k.provizija_adms_eur, locale)}</td></tr>
                                        ) : null}
                                        {k.amortizacija_eur > 0 ? (
                                            <tr><td>{t('D&A', 'D&A')}</td><td style={{ textAlign: 'right' }}>−{formatMoney(k.amortizacija_eur, locale)}</td></tr>
                                        ) : null}
                                        {k.financijski_rashodi_eur > 0 ? (
                                            <tr><td>{t('Financijski rashodi', 'Financial expenses')}</td><td style={{ textAlign: 'right' }}>−{formatMoney(k.financijski_rashodi_eur, locale)}</td></tr>
                                        ) : null}
                                        <tr className="ado-pnl__bridge-sum"><td><strong>EBITDA</strong></td><td style={{ textAlign: 'right' }}><strong>{formatMoney(k.ebitda_eur, locale)}</strong></td></tr>
                                        <tr style={{ color: 'rgba(0,0,0,0.55)', fontSize: '0.82rem' }}><td>margin</td><td style={{ textAlign: 'right' }}>{k.ebitda_pct !== null ? `${k.ebitda_pct.toFixed(2)}%` : '—'}</td></tr>
                                    </tbody>
                                </table>
                            </article>
                            <article className="ado-pnl__forecast-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <p className="ado-pnl__eyebrow">{t('Δ Tabak − Knjigovodstveno (EBITDA)', 'Δ Tabak − Accounting (EBITDA)')}</p>
                                <p className="ado-pnl__forecast-figure" style={{ color: Math.abs(delta) < c.pnl.ebitda_eur * 0.2 ? '#1f7a36' : '#b14040' }}>
                                    {delta >= 0 ? '+' : ''}{formatMoney(delta, locale)}
                                </p>
                                <p className="muted" style={{ fontSize: '0.82rem' }}>
                                    {Math.abs(delta) < Math.abs(c.pnl.ebitda_eur) * 0.2
                                        ? t('Procjena tabak relativno blizu (Δ < 20%).', 'Estimate relatively close (Δ < 20%).')
                                        : t('Velika razlika — tabak procjena divergira.', 'Large delta — estimate diverges.')}
                                </p>
                                {k.method === 'spread' && k.tabak_margin_used ? (
                                    <p className="muted" style={{ fontSize: '0.72rem', marginTop: 4 }}>
                                        {t('Korišten spread:', 'Spread used:')} {k.tabak_margin_used.toFixed(3)}%
                                    </p>
                                ) : null}
                                {k.intercompany_eliminated_eur && k.intercompany_eliminated_eur > 0 ? (
                                    <p className="muted" style={{ fontSize: '0.72rem', marginTop: 4 }}>
                                        ℹ️ {t('Eliminirano intercompany:', 'Intercompany eliminated:')} {formatMoney(k.intercompany_eliminated_eur, locale)}
                                    </p>
                                ) : null}
                            </article>
                        </div>
                        <p className="muted" style={{ fontSize: '0.78rem', marginTop: '0.5rem' }}>{k.note}</p>
                    </section>
                );
            })() : null}

            {/* Forecast vs Actual — historical-ratio model */}
            <section className="ado-pnl__section">
                <div className="ado-pnl__section-heading">
                    <h3>{t('Forecast model — povijesni omjeri', 'Forecast model — historical ratios')}</h3>
                    <p className="muted">
                        {t('Računanjem iz bridge revenue × η_total (RUC/Promet) ± varijanca, kalibrirano na', 'From bridge revenue × η_total (RUC/Revenue) ± variance, calibrated on')}{' '}
                        <strong>{c.forecast.sample_months}</strong> {t('mjeseci', 'months')} ({c.forecast.sample_label}).
                    </p>
                </div>
                <div className="ado-pnl__forecast-grid">
                    <article className="ado-pnl__forecast-card">
                        <p className="ado-pnl__eyebrow">{t('Bridge Revenue (GL)', 'Bridge Revenue (GL)')}</p>
                        <p className="ado-pnl__forecast-figure">{formatMoney(c.forecast.bridge_revenue_eur, locale)}</p>
                        <p className="muted" style={{ fontSize: '0.78rem' }}>
                            × {c.forecast.bridge_to_promet_multiplier.toFixed(4)} {t('(extra Promet od zalog/mjenj.)', '(extra Promet from pawn/exch.)')}
                        </p>
                    </article>
                    <article className="ado-pnl__forecast-card">
                        <p className="ado-pnl__eyebrow">{t('Predviđen Bruto Promet', 'Forecast Gross Revenue')}</p>
                        <p className="ado-pnl__forecast-figure">{formatMoney(c.forecast.promet_forecast_eur, locale)}</p>
                        {c.data_source.mode === 'actual' ? (() => {
                            const d = delta(c.forecast.promet_forecast_eur, c.pnl.revenue_eur);
                            return <p className={`muted tone-${d.tone}`} style={{ fontSize: '0.78rem' }}>vs actual {formatMoney(c.pnl.revenue_eur, locale)} ({d.pct !== null ? `${d.pct >= 0 ? '+' : ''}${d.pct.toFixed(2)}%` : '—'})</p>;
                        })() : <p className="muted" style={{ fontSize: '0.78rem' }}>{t('koristi se kao Bruto Promet', 'used as Gross Revenue')}</p>}
                    </article>
                    <article className="ado-pnl__forecast-card">
                        <p className="ado-pnl__eyebrow">η_total (RUC/Promet)</p>
                        <p className="ado-pnl__forecast-figure">{(c.forecast.eta_total * 100).toFixed(3)}%</p>
                        <p className="muted" style={{ fontSize: '0.78rem' }}>
                            ±{(c.forecast.eta_stddev * 100).toFixed(3)}% σ
                            {c.forecast.eta_cov_pct !== null ? ` · CoV ${c.forecast.eta_cov_pct.toFixed(1)}%` : ''}
                        </p>
                    </article>
                    <article className="ado-pnl__forecast-card">
                        <p className="ado-pnl__eyebrow">{t('Predviđen RUC ±1σ', 'Forecast RUC ±1σ')}</p>
                        <p className="ado-pnl__forecast-figure">{formatMoney(c.forecast.ruc_forecast_eur, locale)}</p>
                        <p className="muted" style={{ fontSize: '0.78rem' }}>
                            {formatMoney(c.forecast.ruc_forecast_low_eur, locale)} – {formatMoney(c.forecast.ruc_forecast_high_eur, locale)}
                        </p>
                        {c.data_source.mode === 'actual' ? (() => {
                            const d = delta(c.forecast.ruc_forecast_eur, c.tabak.bruto_ruc);
                            return <p className={`muted tone-${d.tone}`} style={{ fontSize: '0.78rem' }}>vs actual {formatMoney(c.tabak.bruto_ruc, locale)} ({d.pct !== null ? `${d.pct >= 0 ? '+' : ''}${d.pct.toFixed(2)}%` : '—'})</p>;
                        })() : null}
                    </article>
                </div>
                <div className="ado-pnl__forecast-detail">
                    <div>
                        <p className="ado-pnl__eyebrow">{t('Predviđen RUC po kategoriji', 'Forecast RUC by category')}</p>
                        <table className="ado-pnl__bridge-table">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', fontSize: '0.72rem', color: 'rgba(0,0,0,0.55)' }}>{t('Kategorija', 'Category')}</th>
                                    <th style={{ textAlign: 'right', padding: '0.3rem 0.4rem', fontSize: '0.72rem', color: 'rgba(0,0,0,0.55)' }}>{t('Predviđeno', 'Forecast')}</th>
                                    {c.data_source.mode === 'actual' ? (
                                        <th style={{ textAlign: 'right', padding: '0.3rem 0.4rem', fontSize: '0.72rem', color: 'rgba(0,0,0,0.55)' }}>{t('Stvarno', 'Actual')}</th>
                                    ) : null}
                                </tr>
                            </thead>
                            <tbody>
                                {KAT_LABELS.map((kat) => {
                                    const v = c.forecast.by_kategorija[kat.key] ?? 0;
                                    const actual = c.data_source.mode === 'actual'
                                        ? (['ADMS', 'UREDI', 'DIGITAL'] as Channel[]).reduce((s, ch) => s + (c.tabak.by_channel[ch]?.[kat.key] ?? 0), 0)
                                        : null;
                                    return (
                                        <tr key={kat.key}>
                                            <td><strong>{locale === 'en' ? kat.en : kat.hr}</strong></td>
                                            <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatMoney(v, locale)}</td>
                                            {actual !== null ? <td style={{ textAlign: 'right', color: 'rgba(0,0,0,0.6)', fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums' }}>{formatMoney(actual, locale)}</td> : null}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div>
                        <p className="ado-pnl__eyebrow">{t('Predviđen RUC po kanalu', 'Forecast RUC by channel')}</p>
                        <table className="ado-pnl__bridge-table">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', fontSize: '0.72rem', color: 'rgba(0,0,0,0.55)' }}>{t('Kanal', 'Channel')}</th>
                                    <th style={{ textAlign: 'right', padding: '0.3rem 0.4rem', fontSize: '0.72rem', color: 'rgba(0,0,0,0.55)' }}>{t('Predviđeno', 'Forecast')}</th>
                                    {c.data_source.mode === 'actual' ? (
                                        <th style={{ textAlign: 'right', padding: '0.3rem 0.4rem', fontSize: '0.72rem', color: 'rgba(0,0,0,0.55)' }}>{t('Stvarno', 'Actual')}</th>
                                    ) : null}
                                </tr>
                            </thead>
                            <tbody>
                                {(['ADMS', 'UREDI', 'DIGITAL'] as Channel[]).map((ch) => {
                                    const v = c.forecast.by_kanal[ch] ?? 0;
                                    const actual = c.data_source.mode === 'actual'
                                        ? Object.values(c.tabak.by_channel[ch] ?? {}).reduce((s, x) => s + x, 0)
                                        : null;
                                    return (
                                        <tr key={ch}>
                                            <td><strong>{CHANNEL_META[ch].icon} {locale === 'en' ? CHANNEL_META[ch].en : CHANNEL_META[ch].hr}</strong></td>
                                            <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatMoney(v, locale)}</td>
                                            {actual !== null ? <td style={{ textAlign: 'right', color: 'rgba(0,0,0,0.6)', fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums' }}>{formatMoney(actual, locale)}</td> : null}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
                <p className="muted" style={{ fontSize: '0.82rem', marginTop: '0.6rem' }}>
                    ℹ️ {c.forecast.note}
                </p>
            </section>

            {/* OPEX breakdown */}
            <section className="ado-pnl__section">
                <div className="ado-pnl__section-heading">
                    <h3>{t('OPEX po RRIF grupama (klasa 40-49)', 'OPEX by RRIF groups (class 40-49)')}</h3>
                    <p className="muted">{t('Net D-P iz GL-a, agregirano po klasa 2-cifre grupama.', 'Net D-P from GL, aggregated by 2-digit class groups.')}</p>
                </div>
                <div className="ado-pnl__opex-grid">
                    {c.opex.breakdown.map((g) => (
                        <details key={g.group} className={`ado-pnl__opex-group${g.is_forecast ? ' is-forecast' : ''}`}>
                            <summary>
                                <strong>
                                    {g.group} — {g.label}
                                    {g.is_forecast ? <span style={{ marginLeft: 8, padding: '1px 6px', borderRadius: 4, background: '#fef3c7', color: '#92400e', fontSize: '0.65rem', fontWeight: 700 }}>🔮 FORECAST</span> : null}
                                </strong>
                                <span className="ado-pnl__opex-amount">{formatMoney(g.iznos, locale)}</span>
                            </summary>
                            <ul className="ado-pnl__opex-konti">
                                {g.konti.slice(0, 10).map((k) => (
                                    <li key={k.konto}>
                                        <span><strong>{k.konto}</strong> {k.naziv ? `· ${k.naziv}` : ''}</span>
                                        <span>{formatMoney(k.iznos, locale)}</span>
                                    </li>
                                ))}
                                {g.konti.length > 10 ? <li className="muted">… {g.konti.length - 10} {t('drugih konti', 'more accounts')}</li> : null}
                            </ul>
                        </details>
                    ))}
                </div>
                <p className="ado-pnl__opex-total">{t('Ukupni OPEX', 'Total OPEX')}: <strong>{formatMoney(c.opex.total_eur, locale)}</strong></p>
            </section>

            {/* RUC matrix */}
            <section className="ado-pnl__section">
                <div className="ado-pnl__section-heading">
                    <h3>{t('Bruto marža (RUC) po kanalu × kategoriji', 'Gross margin (RUC) by channel × category')}</h3>
                </div>
                <div className="ado-pnl__matrix">
                    <table className="ado-pnl__matrix-table">
                        <thead>
                            <tr>
                                <th>{t('Kategorija', 'Category')}</th>
                                {(['ADMS', 'UREDI', 'DIGITAL'] as Channel[]).map((ch) => (
                                    <th key={ch}>{CHANNEL_META[ch].icon} {locale === 'en' ? CHANNEL_META[ch].en : CHANNEL_META[ch].hr}</th>
                                ))}
                                <th>{t('Total', 'Total')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {KAT_LABELS.map((kat) => {
                                let rowTotal = 0;
                                return (
                                    <tr key={kat.key}>
                                        <td><strong>{locale === 'en' ? kat.en : kat.hr}</strong></td>
                                        {(['ADMS', 'UREDI', 'DIGITAL'] as Channel[]).map((ch) => {
                                            const v = c.tabak.by_channel[ch]?.[kat.key] ?? 0;
                                            rowTotal += v;
                                            return <td key={ch}>{v ? formatMoney(v, locale) : '—'}</td>;
                                        })}
                                        <td><strong>{rowTotal ? formatMoney(rowTotal, locale) : '—'}</strong></td>
                                    </tr>
                                );
                            })}
                            <tr className="ado-pnl__matrix-foot">
                                <td><strong>{t('Total kanal', 'Channel total')}</strong></td>
                                {(['ADMS', 'UREDI', 'DIGITAL'] as Channel[]).map((ch) => {
                                    const tot = Object.values(c.tabak.by_channel[ch] ?? {}).reduce((s, v) => s + v, 0);
                                    return <td key={ch}><strong>{formatMoney(tot, locale)}</strong></td>;
                                })}
                                <td><strong>{formatMoney(c.tabak.bruto_ruc, locale)}</strong></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* B2B */}
            <section className="ado-pnl__section">
                <div className="ado-pnl__section-heading">
                    <h3>{t('B2B Export (detekcija iz IRA)', 'B2B Export (from outgoing invoices)')}</h3>
                </div>
                <div className="ado-pnl__b2b-row">
                    <B2BTile flag="🇧🇬" title={t('Bugarska (EAM/MFG)', 'Bulgaria (EAM/MFG)')} payload={c.b2b.bugarska} compare={cmp?.b2b.bugarska} locale={locale} />
                    <B2BTile flag="🇸🇮" title={t('Slovenija (Goldman Graff)', 'Slovenia (Goldman Graff)')} payload={c.b2b.slovenija} compare={cmp?.b2b.slovenija} locale={locale} />
                    <B2BTile flag="🔁" title={t('Intercompany (Auro Domus)', 'Intercompany (Auro Domus)')} payload={c.b2b.intercompany} compare={cmp?.b2b.intercompany} locale={locale} />
                </div>
            </section>

            {/* Trend */}
            <section className="ado-pnl__section">
                <div className="ado-pnl__section-heading">
                    <h3>{t('Mjesečni trend RUC', 'Monthly gross margin trend')} — {year}</h3>
                </div>
                <LineChart points={payload.monthly_series.map((m) => ({ date: m.month_label, net: m.total }))} />
            </section>

            {/* Top branches */}
            <section className="ado-pnl__section">
                <div className="ado-pnl__section-heading">
                    <h3>{t('Top poslovnice', 'Top branches')} ({periodLabelHr} {year})</h3>
                </div>
                <ol className="ado-pnl__leaderboard">
                    {payload.top_branches.map((b, idx) => {
                        const max = payload.top_branches[0]?.ruc ?? 1;
                        const pct = Math.min(100, (b.ruc / max) * 100);
                        const accent = CHANNEL_META[b.kanal as Channel]?.accent ?? '#94a3b8';
                        return (
                            <li key={`${b.poslovnica}-${idx}`} className="ado-pnl__lb-item">
                                <span className="ado-pnl__lb-rank">#{idx + 1}</span>
                                <div className="ado-pnl__lb-body">
                                    <div className="ado-pnl__lb-row1">
                                        <strong>{b.poslovnica}</strong>
                                        <span className="ado-pnl__lb-amount">{formatMoney(b.ruc, locale)}</span>
                                    </div>
                                    <div className="ado-pnl__lb-bar"><span style={{ width: `${pct}%`, background: accent }} /></div>
                                    <p className="muted ado-pnl__lb-meta">{b.kanal} {b.grad ? '· '+b.grad : ''}{b.voditelj ? ' · '+b.voditelj : ''}</p>
                                </div>
                            </li>
                        );
                    })}
                </ol>
            </section>
        </div>
    );
}

function PnlLine({ label, value, compare, locale, bold, tone, indent, subtotal, pct }: {
    label: string; value: number; compare?: number; locale: 'hr' | 'en';
    bold?: boolean; tone?: 'pos' | 'neg'; indent?: boolean; subtotal?: boolean; pct?: number | null;
}) {
    const d = compare !== undefined ? delta(value, compare) : null;
    return (
        <div className={`ado-pnl__waterfall-row${bold ? ' is-bold' : ''}${subtotal ? ' is-subtotal' : ''}${indent ? ' is-indent' : ''}`}>
            <span className="ado-pnl__waterfall-label">{label}</span>
            <span className={`ado-pnl__waterfall-value tone-${tone ?? 'neutral'}`}>{formatMoney(value, locale)}</span>
            {pct !== undefined && pct !== null ? <span className="ado-pnl__waterfall-pct">{pct.toFixed(2)}%</span> : <span />}
            {d ? (
                <span className={`ado-pnl__waterfall-delta tone-${d.tone}`}>
                    {d.tone === 'pos' ? '▲' : d.tone === 'neg' ? '▼' : '·'} {d.pct !== null ? `${d.pct >= 0 ? '+' : ''}${d.pct.toFixed(1)}%` : '—'}
                </span>
            ) : <span />}
        </div>
    );
}

function B2BTile({ flag, title, payload, compare, locale }: {
    flag: string; title: string; payload: { count: number; total_eur: number }; compare?: { count: number; total_eur: number }; locale: 'hr' | 'en';
}) {
    const d = compare ? delta(payload.total_eur, compare.total_eur) : null;
    return (
        <article className="ado-pnl__b2b-tile">
            <p className="ado-pnl__b2b-flag" aria-hidden="true">{flag}</p>
            <p className="ado-pnl__eyebrow">{title}</p>
            <p className="ado-pnl__b2b-amount">{formatMoney(payload.total_eur, locale)}</p>
            <p className="muted" style={{ fontSize: '0.82rem' }}>{formatNumber(payload.count, locale)} {locale === 'en' ? 'invoices' : 'faktura'}</p>
            {d ? (
                <p className={`ado-pnl__kpi-delta tone-${d.tone}`}>
                    {d.tone === 'pos' ? '▲' : d.tone === 'neg' ? '▼' : '·'} {d.pct !== null ? `${d.pct >= 0 ? '+' : ''}${d.pct.toFixed(1)}%` : ''} vs prev
                </p>
            ) : null}
        </article>
    );
}
